import React, { useContext, useEffect, useState } from 'react'
import { Button, Input, Space, Table } from 'antd'
import ReactQuill from 'react-quill'
import client from 'apollo'
import { EyeOutlined, EditOutlined } from '@ant-design/icons'
import 'react-quill/dist/quill.snow.css'
import {
  CREATE_TRIGGERED_MAIL_TEMPLATE,
  UPDATE_TEMPLATE_DATA,
  UPDATE_TRIGGERED_MAIL_TEMPLATE,
} from '../graphql/Mutations'
import {
  GET_TEMPLATE_DATA,
  GET_TRIGGERED_MAIL_TEMPLATE,
  // GET_TRIGGERED_MAIL_TEMPLATE_BY_ID,
} from '../graphql/Queries'
import openNotification from 'utils/Notification'
import CustomMeta from 'components/CustomMeta'
import TriggeredMailModal from './triggerModal.js'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const TriggerMailFormat = () => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)

  const [active, setActive] = useState('VERIFICATION_STUCK')
  const [loading, setLoading] = useState(false)
  const [subject, setSubject] = useState()
  const [error, setError] = useState(false)
  const [templateHtml, setTemplateHtml] = useState('')
  // eslint-disable-next-line
  const [updatedTriggeredData, setUpdatedTriggeredData] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  // eslint-disable-next-line
  const [createTriggredTemplateData, setCreateTriggredTemplateData] =
    useState(null)
  const [tableData, setTableData] = useState([])
  const [isTemplateVisible, setIsTemplateVisible] = useState(false)

  useEffect(() => {
    if (active) {
      client
        .query({
          query: GET_TEMPLATE_DATA,
          variables: { type: active },
          fetchPolicy: 'network-only',
        })
        .then((res) => {
          setTemplateHtml(res?.data?.getTemplateData?.htmlData)
          setSubject(res?.data?.getTemplateData?.subject)
        })
        .catch((err) => {
          console.error('Failed to fetch template on type change:', err)
        })
    }
  }, [active])

  const getTriggeredData = () => {
    client
      .query({
        query: GET_TRIGGERED_MAIL_TEMPLATE,
        variables: { limit: 10, skip: 0 },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        const result = res?.data?.getTriggeredMailTemplates || []
        const templates = result?.[0]?.templates || []

        const formattedTemplates = templates.map((template, index) => ({
          key: index,
          ...template,
        }))

        setTableData(formattedTemplates)
      })
      .catch((error) => {
        console.error('Error fetching triggered mail templates:', error)
      })
  }


  const createTriggeredMailTemplate = ({ name, interval, type }) => {
    if (!hasAdd || !hasSave) return
    try {
      client
        .mutate({
          mutation: CREATE_TRIGGERED_MAIL_TEMPLATE,
          variables: {
            data: {
              triggeredName: name,
              triggeredInterval: interval,
              triggeredType: type,
            },
            fetchPolicy: 'network-only',
          },
        })
        .then((res) => {
          setCreateTriggredTemplateData(res.data.createTriggeredMailTemplate)
          getTriggeredData()
        })
    } catch (error) {
      console.error(error)
    }
  }

  const updateTriggerMailTemplate = ({ id, name, interval, type }) => {
    if (!hasEdit || !hasSave) return
    client
      .mutate({
        mutation: UPDATE_TRIGGERED_MAIL_TEMPLATE,
        variables: {
          data: {
            triggeredName: name,
            triggeredInterval: interval,
            triggeredType: type,
          },
          where: id,
        },
        fetchPolicy: 'no-cache',
      })
      .then((res) => {
        getTriggeredData()
        setUpdatedTriggeredData((prevData) =>
          prevData.map((item) =>
            item.id === id
              ? {
                  ...item,
                  triggeredName: name,
                  triggeredInterval: interval,
                  triggeredType: type,
                }
              : item
          )
        )
      })
      .catch((err) => {
        console.error('Update failed:', err)
      })
  }

  useEffect(() => {
    getTriggeredData()
  }, [])

  useEffect(() => {
    if (!isTemplateVisible) {
      setTemplateHtml('')
      setSubject('')
      setError(false)
    }
  }, [isTemplateVisible])

  const columns = [
    {
      title: 'Trigger Name',
      dataIndex: 'triggeredName',
      key: 'triggeredName',
      width: 180,
    },
    {
      title: 'Trigger Interval',
      dataIndex: 'triggeredInterval',
      key: 'triggeredInterval',
      width: 150,
    },
    {
      title: 'Trigger Type',
      dataIndex: 'triggeredType',
      key: 'triggeredType',
      width: 180,
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (text, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <Button
            type='primary'
            size='small'
            style={{ marginBottom: '0px' }}
            icon={<EyeOutlined />}
            onClick={() => {
              console.log(
                'View triggered mail:',
                record,
                'recordds',
                record.triggeredType
              )
              const type = record?.triggeredType

              if (type === active && isTemplateVisible) {
                setIsTemplateVisible(false)
                return
              }

              setActive(type)
              setIsTemplateVisible(true)

              client
                .query({
                  query: GET_TEMPLATE_DATA,
                  variables: { type },
                  fetchPolicy: 'network-only',
                })
                .then((res) => {
                  setTemplateHtml(res?.data?.getTemplateData?.htmlData)
                  setSubject(res?.data?.getTemplateData?.subject)
                })
                .catch((err) => {
                  console.error('Failed to fetch template:', err)
                })
            }}
          >
            View
          </Button>
          {hasEdit && (
            <Button
              type='default'
              size='small'
              style={{ marginBottom: '0px' }}
              icon={<EditOutlined />}
              onClick={() => {
                setIsModalVisible(true)
                setIsEditing(true)
                setSelectedData(record)
              }}
            >
              Edit
            </Button>
          )}
        </div>
      ),
    },
  ]

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['clean'],
    ],
  }

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'background',
    'image',
    'video',
    'align',
    'direction',
    'code-block',
    'formula',
  ]

  const handleValue = (content, delta, source, editor) => {
    setTemplateHtml(editor.getHTML())
  }

  const handelSubject = (value) => {
    setError(false)
    setSubject(value.target.value)
  }

  const handleBlur = () => {
    if (!subject.trim()) {
      setError(true)
    }
  }

  const updateCode = () => {
    if (!hasEdit || !hasSave) return
    setLoading(true)
    client
      .mutate({
        mutation: UPDATE_TEMPLATE_DATA,
        variables: {
          type: active,
          htmlData: templateHtml,
          subject: subject,
        },
      })
      .then(() => {
        setLoading(false)
        openNotification('success', 'Mail Format Changed Successfully')
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })
  }

  // const handleChange = (value) => {
  //   setActive(value)
  // }

  // const options = [
  //   { value: 'VERIFICATION_STUCK', label: 'VERIFICATION_STUCK' },
  //   { value: 'NON_VERIFICATION_STUCK', label: 'NON_VERIFICATION_STUCK' },
  //   { value: 'PENDING_ACTION_ADVISORY', label: 'PENDING_ACTION_ADVISORY' },
  //   {
  //     value: 'SECOND_PENDING_ACTION_ADVISORY',
  //     label: 'SECOND_PENDING_ACTION_ADVISORY',
  //   },
  // ]

  return (
    <div className='main_dashboard_div'>
      <CustomMeta title='Trigger Mail Format' />
      <div className='agreement_main_div'>
        <div className='agreement_text'>Trigger Mail Format</div>
        {/* <Select
          style={{ width: 210, marginTop: 15 }}
          getPopupContainer={(trigger) => trigger.parentElement}
          value={active}
          placeholder='Tags Mode'
          onChange={handleChange}
          options={options}
        /> */}
        {hasAdd && (
          <Button
            type='primary'
            style={{ marginBottom: '0px' }}
            onClick={() => {
              setIsModalVisible(true)
              setIsEditing(false)
              setSelectedData(null)
            }}
          >
            Add Triggered Mail
          </Button>
        )}
      </div>

      {/* Table Section */}
      <div style={{ margin: '30px 0' }}>
        <Table
          columns={columns}
          dataSource={tableData}
          bordered
          size='middle'
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* Modal section */}
      <TriggeredMailModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setSelectedData(null)
          setIsEditing(false)
        }}
        onCreate={(values, resetFields) => {
          createTriggeredMailTemplate({
            name: values.triggeredName,
            interval: values.triggeredInterval,
            type: values.triggeredType,
          })

          resetFields()
          setIsModalVisible(false)
          setSelectedData(null)
          setIsEditing(false)
        }}
        onUpdate={(values, resetFields) => {
          updateTriggerMailTemplate({
            id: selectedData.id,
            name: values.triggeredName,
            interval: values.triggeredInterval,
            type: values.triggeredType,
          })

          resetFields()
          setIsModalVisible(false)
          setSelectedData(null)
          setIsEditing(false)
        }}
        selectedData={selectedData}
        isEdit={isEditing}
        loading={loading}
      />

      {/* Template Editor Section */}
      {isTemplateVisible && (
        <div>
          <Space
            direction='vertical'
            style={{ marginBottom: '15px', width: '100%' }}
          >
            <Input
              addonBefore='Subject'
              onChange={handelSubject}
              value={subject}
              status={error ? 'error' : ''}
              onBlur={handleBlur}
            />
            {error && <p style={{ color: 'red' }}>*Subject is required</p>}
          </Space>

          <ReactQuill
            theme='snow'
            value={templateHtml}
            onChange={handleValue}
            modules={modules}
            formats={formats}
          />

          <div className='agreement_btn_div'>
            <Button
              onClick={updateCode}
              type='primary'
              className='agreement_submit_btn'
              loading={loading}
              disabled={!hasEdit || !hasSave}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TriggerMailFormat
